"use strict";
/**
 * Mathematical functions for Riemann Sums visualization
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDefaultFunctions = exports.getFunctionString = exports.calculateExactIntegral = exports.calculateRiemannSum = exports.evaluateFunction = void 0;
/**
 * Evaluate a function at a given x value
 */
function evaluateFunction(x, config) {
    switch (config.type) {
        case 'quadratic':
            return config.a * x * x + config.b * x + config.c;
        case 'cubic':
            return config.a * x * x * x + config.b * x * x + config.c * x;
        case 'exponential':
            return config.a * Math.exp(config.b * x) + config.c;
        case 'logarithmic':
            return config.a * Math.log(Math.abs(x) + 0.1) + config.c; // Add small value to avoid log(0)
        case 'trigonometric':
            return config.amplitude * Math.sin(config.frequency * x) + config.c;
        case 'polynomial':
            return config.a * x * x * x * x + config.b * x * x * x + config.c * x * x + x;
        default:
            return 0;
    }
}
exports.evaluateFunction = evaluateFunction;
/**
 * Calculate Riemann sum using specified method
 */
function calculateRiemannSum(func, a, // lower bound
b, // upper bound
n, // number of subintervals
method) {
    const dx = (b - a) / n;
    const rectangles = [];
    let sum = 0;
    for (let i = 0; i < n; i++) {
        const xLeft = a + i * dx;
        const xRight = a + (i + 1) * dx;
        const xMid = (xLeft + xRight) / 2;
        let height;
        let x;
        switch (method) {
            case 'left':
                height = evaluateFunction(xLeft, func);
                x = xLeft;
                break;
            case 'right':
                height = evaluateFunction(xRight, func);
                x = xRight;
                break;
            case 'midpoint':
                height = evaluateFunction(xMid, func);
                x = xMid;
                break;
            case 'trapezoidal':
                const leftHeight = evaluateFunction(xLeft, func);
                const rightHeight = evaluateFunction(xRight, func);
                height = (leftHeight + rightHeight) / 2;
                x = xMid;
                break;
        }
        const area = height * dx;
        sum += area;
        rectangles.push({
            x: x,
            width: dx,
            height: height,
            area: area
        });
    }
    return { sum, rectangles };
}
exports.calculateRiemannSum = calculateRiemannSum;
/**
 * Calculate the exact integral using analytical methods (where possible)
 */
function calculateExactIntegral(func, a, b) {
    switch (func.type) {
        case 'quadratic':
            // ∫(ax² + bx + c)dx = (a/3)x³ + (b/2)x² + cx
            const antiderivative = (x) => (func.a / 3) * x * x * x + (func.b / 2) * x * x + func.c * x;
            return antiderivative(b) - antiderivative(a);
        case 'cubic':
            // ∫(ax³ + bx² + cx)dx = (a/4)x⁴ + (b/3)x³ + (c/2)x²
            const cubicAntiderivative = (x) => (func.a / 4) * x * x * x * x + (func.b / 3) * x * x * x + (func.c / 2) * x * x;
            return cubicAntiderivative(b) - cubicAntiderivative(a);
        case 'exponential':
            // ∫(a*e^(bx) + c)dx = (a/b)*e^(bx) + cx
            if (func.b !== 0) {
                const expAntiderivative = (x) => (func.a / func.b) * Math.exp(func.b * x) + func.c * x;
                return expAntiderivative(b) - expAntiderivative(a);
            }
            return func.c * (b - a);
        case 'trigonometric':
            // ∫(A*sin(fx) + c)dx = -(A/f)*cos(fx) + cx
            if (func.frequency !== 0) {
                const trigAntiderivative = (x) => -(func.amplitude / func.frequency) * Math.cos(func.frequency * x) + func.c * x;
                return trigAntiderivative(b) - trigAntiderivative(a);
            }
            return func.c * (b - a);
        default:
            // For functions without analytical solutions, use numerical integration
            return numericalIntegration(func, a, b);
    }
}
exports.calculateExactIntegral = calculateExactIntegral;
/**
 * Numerical integration using Simpson's rule for functions without analytical solutions
 */
function numericalIntegration(func, a, b) {
    const n = 1000; // High number of intervals for accuracy
    const dx = (b - a) / n;
    let sum = 0;
    for (let i = 0; i < n; i++) {
        const x1 = a + i * dx;
        const x2 = a + (i + 1) * dx;
        const xMid = (x1 + x2) / 2;
        // Simpson's rule: (f(x1) + 4f(xMid) + f(x2)) * dx/6
        const f1 = evaluateFunction(x1, func);
        const fMid = evaluateFunction(xMid, func);
        const f2 = evaluateFunction(x2, func);
        sum += (f1 + 4 * fMid + f2) * dx / 6;
    }
    return sum;
}
/**
 * Get function display string
 */
function getFunctionString(config) {
    switch (config.type) {
        case 'quadratic':
            return `f(x) = ${config.a}x² + ${config.b}x + ${config.c}`;
        case 'cubic':
            return `f(x) = ${config.a}x³ + ${config.b}x² + ${config.c}x`;
        case 'exponential':
            return `f(x) = ${config.a}e^(${config.b}x) + ${config.c}`;
        case 'logarithmic':
            return `f(x) = ${config.a}ln(|x|) + ${config.c}`;
        case 'trigonometric':
            return `f(x) = ${config.amplitude}sin(${config.frequency}x) + ${config.c}`;
        case 'polynomial':
            return `f(x) = ${config.a}x⁴ + ${config.b}x³ + ${config.c}x² + x`;
        default:
            return 'f(x) = 0';
    }
}
exports.getFunctionString = getFunctionString;
/**
 * Get default function configurations
 */
function getDefaultFunctions() {
    return {
        'Simple Quadratic': { type: 'quadratic', a: 1, b: 0, c: 0 },
        'Shifted Quadratic': { type: 'quadratic', a: 1, b: -2, c: 1 },
        'Cubic Function': { type: 'cubic', a: 0.5, b: -2, c: 1 },
        'Exponential': { type: 'exponential', a: 1, b: 0.5, c: 0 },
        'Logarithmic': { type: 'logarithmic', a: 1, b: 0, c: 0 },
        'Sine Wave': { type: 'trigonometric', a: 0, b: 0, c: 0, amplitude: 2, frequency: 1 },
        'Polynomial': { type: 'polynomial', a: 0.1, b: -0.5, c: 1 }
    };
}
exports.getDefaultFunctions = getDefaultFunctions;
